import { useSigma } from "react-sigma-v2";
import { FC, useEffect } from "react";
import { inRange } from "lodash";

import {
  FiltersState,
  SigmaEdge,
  SigmaHasTagEdge,
  SigmaIsLinkedEdge,
  SigmaItemNode,
  SigmaNode,
  SigmaTagNode,
  WYMMO_HAS_TAG,
  WYMMO_IS_LINKED,
  WYMMO_ITEM,
  WYMMO_TAG,
  WymmoDirectory,
  WymmoTag,
  get_tag_group_color,
} from "../types";
import Graph from "graphology";
import { DEFAULT_SETTINGS } from "sigma/settings";

const DEFAULT_NODE_ATTRIBUTES = {
  // Sigma specific attributes:
  size: 1,
  color: DEFAULT_SETTINGS.defaultNodeColor,
  x: 0,
  y: 0,
  hidden: false,
  forceLabel: false,
  zIndex: 0,
  type: DEFAULT_SETTINGS.defaultNodeType,
  highlighted: false,
};

// Node styles:
const ITEM_NODE_SIZE = 4;
export const ITEM_NODE_COLOR = "lightgrey";
const ITEM_WITH_ICON_NODE_COLOR = "#F9F9F9";
const TAG_NODE_SIZE = 8;

// Edge styles:
const HAS_TAG_EDGE_SIZE = 1;
const HAS_TAG_EDGE_COLOR = "lightgrey";
const IS_LINKED_EDGE_SIZE = 1.5;
const IS_LINKED_EDGE_COLOR = "lightgrey";
export const HIGHLIGHTED_EDGE_SIZE = 2;

const ADD_TAGS = true;

enum GraphTagsOrdering {
  BY_TAG_GROUP_AND_NAME,
  BY_TAG_LENGTH,
}

const TAG_ORDERING = GraphTagsOrdering.BY_TAG_GROUP_AND_NAME as GraphTagsOrdering;

const GraphDataController: FC<{
  dataset: WymmoDirectory;
  filters: FiltersState;
  date: number;
  iconsRoot: string;
}> = ({ dataset, filters, date, iconsRoot, children, }) => {
  const sigma = useSigma();
  const graph = sigma.getGraph() as Graph<SigmaNode, SigmaEdge>;

  /**
   * Feed graphology with the new dataset:
   */
  useEffect(() => {
    if (!graph || !dataset) return;

    const groupe_tag = dataset.tags.find((tag) => tag.title === "Groupe");
    const non_groupe_tags = dataset.tags.filter(
      (tag) => tag.title !== "Groupe"
    );
    const tag_length = non_groupe_tags.length;

    let tags: [WymmoTag, number][] = [];
    for (let i = 0; i < tag_length; i++) {
      let tag = non_groupe_tags[i];
      let tag_length = tag.title.length;
      tags.push([tag, tag_length]);
    }
    // order by length
    tags.sort((a, b) => a[1] - b[1]);

    if (TAG_ORDERING === GraphTagsOrdering.BY_TAG_GROUP_AND_NAME) {
      // then re-order tags by tag_groups
      tags.sort((a, b) => a[0].tag_group.localeCompare(b[0].tag_group));
    }

    // array of tag positions (radion angles on the circle)
    const positions: [number, number][] = [];
    for (let i = 0; i < tag_length; i++) {
      let randian = ((Math.PI * 2) / tag_length) * i; // Divide the circle into N equal parts
      let sinus = Math.sin(randian);
      positions.push([randian, Math.abs(sinus)]);
    }

    if (TAG_ORDERING === GraphTagsOrdering.BY_TAG_LENGTH) {
      // order by sin desc
      positions.sort((a, b) => b[1] - a[1]);
    }

    function add_tag_to_graph(
      tag: WymmoTag,
      randian: number,
      radius: number = 150
    ) {
      if (!ADD_TAGS) return;

      let icon = null as string | null;
      if (tag.icon) {
        icon = `${iconsRoot}/${tag.icon}`;
      }

      graph.addNode(tag.key, {
        wymmoType: WYMMO_TAG,
        label: tag.title,
        wymmoAttributes: tag,
        ...DEFAULT_NODE_ATTRIBUTES,
        x: Math.cos(randian) * radius,
        y: Math.sin(randian) * radius,
        color: get_tag_group_color(tag.tag_group),
        size: TAG_NODE_SIZE,
        type: tag.icon ? "image" : "circle",
        image: icon,
      } as SigmaTagNode);
    }

    if (groupe_tag) {
      add_tag_to_graph(groupe_tag, 0, 0);
    }

    for (let i = 0; i < tag_length; i++) {
      let tag = tags[i][0];
      let randian = positions[i][0];
      add_tag_to_graph(tag, randian);
    }

    const items_length = dataset.items.length;
    let y = 0;
    dataset.items.forEach((item) => {
      const angle = ((Math.PI * 2) / items_length) * y++;

      let icon = null as string | null;
      if (item.icon) {
        // icon = `http://localhost:5000${process.env.DIRECTORY_ROOT_URL}/icons/${tag.icon}`
        icon = `${iconsRoot}/${item.icon}`;
      }

      const tag_labels = item.tags.map((tag) => dataset.tags.find(t => t.key === tag)?.title);

      graph.addNode(item.key, {
        wymmoType: WYMMO_ITEM,
        label: item.title,
        wymmoAttributes: item,
        wymmoTagLabels: tag_labels,
        ...DEFAULT_NODE_ATTRIBUTES,
        x: Math.cos(angle) * 80,
        y: Math.sin(angle) * 80,
        color: icon ? ITEM_WITH_ICON_NODE_COLOR : ITEM_NODE_COLOR,
        size: ITEM_NODE_SIZE,
        type: item.icon ? "image" : "circle",
        image: icon,
      } as SigmaItemNode);
    });

    dataset.items.forEach((item) => {
      if (ADD_TAGS) {
        item.tags.forEach((tag) => {
          graph.addEdgeWithKey(`${item.key}/${tag}`, item.key, tag, {
            wymmoType: WYMMO_HAS_TAG,
            color: HAS_TAG_EDGE_COLOR,
            size: HAS_TAG_EDGE_SIZE,
          } as SigmaHasTagEdge);
        });
      }

      item.links.forEach((link) => {
        graph.addEdgeWithKey(
          `${item.key}/${link.target_key}`,
          item.key,
          link.target_key,
          {
            wymmoType: WYMMO_IS_LINKED,
            wymmoAttributes: link,
            color: IS_LINKED_EDGE_COLOR,
            size: IS_LINKED_EDGE_SIZE,
          } as SigmaIsLinkedEdge
        );
      });
    });

    return () => graph.clear();
  }, [graph, dataset]);

  /**
   * Apply filters to graphology:
   */
  useEffect(() => {
    const { tags } = filters;

    graph.forEachNode((node, attributes) => {
      let isInGraph = true;
      if (attributes.wymmoType === WYMMO_ITEM) {
        isInGraph =
          (attributes.wymmoAttributes.tags.some((tag) => tags[tag]) ||
            !attributes.wymmoAttributes.tags.length) &&
          inRange(
            date,
            attributes.wymmoAttributes.created_in,
            attributes.wymmoAttributes.concluded_in ? attributes.wymmoAttributes.concluded_in + 1 : Infinity
          );
      } else {
        isInGraph = tags[node];
      }

      graph.setNodeAttribute(node, "hidden", !isInGraph);
    });

    graph.forEachEdge((edge, attributes) => {
      let isInGraph = true;
      if (attributes.wymmoType === WYMMO_IS_LINKED) {
        isInGraph = inRange(
          date,
          attributes.wymmoAttributes.begin_in || -Infinity,
          attributes.wymmoAttributes.end_in || Infinity
        );
      }

      graph.setEdgeAttribute(edge, "hidden", !isInGraph);
    });
  }, [graph, filters, date]);

  return <>{children}</>;
};

export default GraphDataController;
